// Statistics → Parcels → Dashboard: how long parcels take, per carrier and per country (parcel tracking v2,
// Claude, 2026-10-02). Every number comes from backend/parcel_report.py — the same code the connector's
// `parcel_delivery_report` runs — and arrives in BOTH units; the page shows the one chosen, names it in every
// header and in the CSV, and says what was counted. Fernando: "logically explained and separated … so nobody
// can mess this up and accidentally give wrong numbers".
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch, API } from '../api';
import { DatePreset, DATE_PRESETS, weekRange, monthRange, last30Range, allTimeRange } from './shared';
import { CARRIER_CODES, CARRIER_NAME } from '../carriers';

interface Cell {
  n: number; of?: number; not_reported?: boolean;
  avg_days: number | null; median_days: number | null; avg_workdays: number | null; median_workdays: number | null;
  label?: string; span?: string;
  within_expectation?: { limit_workdays: number; share: number; of: number };
}
interface Row {
  carrier: string | null; carrier_name: string; country: string | null; parcels: number;
  pickup_point_parcels: number; home_parcels: number;
  to: Record<string, Cell>; legs: Record<string, Cell>;
}
interface Report {
  start: 'ordered' | 'processed'; start_label: string; rows: Row[]; total: Row | null; parcels_counted: number;
  excluded: Record<string, number>; steps: Record<string, string>;
  legs: Record<string, { label: string; span: string; only: string | null }>;
  notes: { carrier: string; carrier_name: string; note: string }[];
  definitions: { term: string; meaning: string }[];
  workdays_available: boolean; calendar_gaps: string[] | null;
  window: { from: string; to: string };
}
type Unit = 'workdays' | 'days';

const EXCLUDED_TEXT: Record<string, string> = {
  replaced_label_never_shipped: 'replaced labels that never shipped',
  resend_order: 'resend orders',
  steps_out_of_order: 'parcels whose steps are out of order',
};

function num(c: Cell | undefined, unit: Unit, which: 'avg' | 'median' = 'avg') {
  if (!c) return null;
  const v = (c as any)[`${which}_${unit}`];
  return v == null ? null : Number(v);
}

function CellView({ c, unit }: { c?: Cell; unit: Unit }) {
  if (!c) return <span className="text-gray-700">—</span>;
  if (c.not_reported) {
    return <span className="text-gray-600 italic" title="This carrier never reports this step here — not 0, simply not known">not reported</span>;
  }
  const v = num(c, unit);
  if (v == null) return <span className="text-gray-700">—</span>;
  const med = num(c, unit, 'median');
  return (
    <div className="leading-tight">
      <div className="text-white tabular-nums">{v.toFixed(1)}</div>
      <div className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap"
           title={`median ${med ?? '—'} · counted ${c.n} parcels${c.of != null && c.of > c.n ? ` of ${c.of} that got this far — the carrier reported this step for ${c.n}` : ''}`}>
        med {med ?? '—'} · n {c.n}{c.of != null && c.of > c.n ? ` of ${c.of}` : ''}
      </div>
    </div>
  );
}

function csvCell(c: Cell | undefined, unit: Unit) {
  if (!c) return '';
  if (c.not_reported) return 'not reported';
  const v = num(c, unit);
  return v == null ? '' : String(v);
}
function download(name: string, rows: (string | number)[][]) {
  const esc = (x: string | number) => { const s = String(x ?? ''); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const blob = new Blob(['﻿' + rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function ParcelDashboard({ webshops }: { webshops: { id: string; name: string }[] }) {
  const [preset, setPreset] = useState<DatePreset>('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [store, setStore] = useState('');
  const [delivery, setDelivery] = useState('');
  const [start, setStart] = useState<'ordered' | 'processed'>('processed');
  const [unit, setUnit] = useState<Unit>(() => (localStorage.getItem('wms_parcel_unit') as Unit) || 'workdays');
  const [country, setCountry] = useState('');
  const [rep, setRep] = useState<Report | null>(null);
  const [repC, setRepC] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { try { localStorage.setItem('wms_parcel_unit', unit); } catch { /* a convenience only */ } }, [unit]);

  const range = useCallback(() => {
    if (preset === 'week') return weekRange();
    if (preset === 'month') return monthRange();
    if (preset === 'all') return allTimeRange();
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return last30Range();
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    const { from, to } = range();
    if (!from || !to) return;
    setLoading(true); setErr('');
    const qs = `date_from=${from}&date_to=${to}&start=${start}&store=${store}&delivery=${delivery}`;
    try {
      const [a, b] = await Promise.all([
        authFetch(`${API}/tracking/report?${qs}`),
        authFetch(`${API}/tracking/report?${qs}&by_country=true`),
      ]);
      if (!a.ok) { const d = await a.json().catch(() => ({} as any)); setErr(d.detail || `HTTP ${a.status}`); setRep(null); setRepC(null); }
      else { setRep(await a.json()); setRepC(b.ok ? await b.json() : null); }
    } catch { setErr('Could not load the report'); }
    setLoading(false);
  }, [range, start, store, delivery]);
  useEffect(() => { load(); }, [load]);

  const U = unit === 'workdays' ? 'workdays' : 'days';
  const steps = rep ? Object.entries(rep.steps) : [];
  const legs = rep ? Object.entries(rep.legs) : [];
  const countries = useMemo(() => Array.from(new Set((repC?.rows || []).map(r => r.country || '?'))).sort(), [repC]);
  const t3rows: Row[] = useMemo(() => {
    if (!repC) return [];
    if (!country) return repC.rows;
    const have = repC.rows.filter(r => (r.country || '?') === country);
    // the selected country: every carrier we have, empty where it doesn't ship there
    return CARRIER_CODES.map(c => have.find(r => r.carrier === c)
      || { carrier: c, carrier_name: CARRIER_NAME[c], country, parcels: 0, pickup_point_parcels: 0, home_parcels: 0, to: {}, legs: {} } as Row);
  }, [repC, country]);

  const fromLabel = start === 'ordered' ? 'ordered' : 'processed';
  const csv1 = () => rep && download(`parcels_table1_${rep.window.from}_${rep.window.to}_from-${fromLabel}_${U}.csv`, [
    ['Carrier', 'Parcels', ...steps.flatMap(([, l]) => [`${fromLabel} → ${l} (avg ${U})`, `${l} n`])],
    ...[...rep.rows, ...(rep.total ? [rep.total] : [])].map(r => [r.carrier_name, r.parcels,
      ...steps.flatMap(([k]) => [csvCell(r.to[k], unit), r.to[k]?.n ?? ''])]),
  ]);
  const csv2 = () => rep && download(`parcels_table2_${rep.window.from}_${rep.window.to}_${U}.csv`, [
    ['Carrier', ...legs.flatMap(([, l]) => [`${l.label}: ${l.span} (avg ${U})`, `${l.label} n`])],
    ...[...rep.rows, ...(rep.total ? [rep.total] : [])].map(r => [r.carrier_name,
      ...legs.flatMap(([k]) => [csvCell(r.legs[k], unit), r.legs[k]?.n ?? ''])]),
  ]);
  const csv3 = () => repC && download(`parcels_table3_${repC.window.from}_${repC.window.to}_from-${fromLabel}_${U}${country ? '_' + country : ''}.csv`, [
    ['Country', 'Carrier', 'Parcels', ...steps.flatMap(([, l]) => [`${fromLabel} → ${l} (avg ${U})`, `${l} n`])],
    ...t3rows.map(r => [r.country || '', r.carrier_name, r.parcels, ...steps.flatMap(([k]) => [csvCell(r.to[k], unit), r.to[k]?.n ?? ''])]),
  ]);

  const seg = (on: boolean) => `px-3 py-1.5 text-sm ${on ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`;
  const sel = 'bg-gray-800 border border-gray-700 text-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500';
  const card = 'mt-5 bg-[#1a1d27] rounded-xl border border-gray-800 p-4';
  const th = 'px-3 py-2 text-left text-gray-500 text-xs font-semibold align-bottom';
  const td = 'px-3 py-2 align-top';
  const unitWord = unit === 'workdays' ? 'workdays' : 'days';

  const Table1Body = ({ rows, withCountry }: { rows: Row[]; withCountry?: boolean }) => (
    <tbody>
      {rows.map((r, i) => {
        const isTotal = r.carrier == null;
        const newCountry = withCountry && (i === 0 || (rows[i - 1].country !== r.country));
        return (
          <tr key={`${r.country}-${r.carrier}-${i}`} className={`border-t border-gray-800/60 ${isTotal ? 'bg-gray-900/40' : ''}`}>
            {withCountry && <td className={`${td} text-gray-300 font-medium`}>{newCountry ? r.country : ''}</td>}
            <td className={`${td} ${isTotal ? 'text-gray-300 font-semibold' : 'text-white'}`}>{r.carrier_name}</td>
            <td className={`${td} text-gray-300 tabular-nums`}>{r.parcels || <span className="text-gray-700">—</span>}</td>
            {steps.map(([k]) => <td key={k} className={td}>{r.parcels ? <CellView c={r.to[k]} unit={unit} /> : <span className="text-gray-700">—</span>}</td>)}
          </tr>
        );
      })}
    </tbody>
  );

  return (
    <div>
      {/* controls */}
      <div className="flex flex-wrap items-center gap-2">
        {DATE_PRESETS.map(p => (
          <button key={p.key} onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 text-sm rounded-lg ${preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className={sel} />
            <span className="text-gray-600">—</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className={sel} />
          </span>
        )}
        {loading && <span className="text-gray-600 text-sm">Loading…</span>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <select value={store} onChange={e => setStore(e.target.value)} className={sel}>
          <option value="">All stores</option>
          {webshops.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={delivery} onChange={e => setDelivery(e.target.value)} className={sel}>
          <option value="">Pickup point + home</option>
          <option value="point">Pickup point only</option>
          <option value="home">Home delivery only</option>
        </select>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs uppercase tracking-wider">Count from</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-700">
            <button className={seg(start === 'ordered')} onClick={() => setStart('ordered')} title="From the moment the customer ordered">Ordered</button>
            <button className={seg(start === 'processed')} onClick={() => setStart('processed')} title="From the moment the parcel left our packing table">Processed</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs uppercase tracking-wider">Count in</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-700">
            <button className={seg(unit === 'workdays')} onClick={() => setUnit('workdays')}>Workdays</button>
            <button className={seg(unit === 'days')} onClick={() => setUnit('days')}>Days</button>
          </div>
        </div>
      </div>

      {err && <div className="mt-4 text-red-400 text-sm">{err}</div>}

      {rep && (
        <>
          {/* what is counted, in words */}
          <div className="mt-4 rounded-xl border border-blue-900/60 bg-blue-950/20 p-4 text-sm">
            <div className="text-blue-200 font-medium">
              {rep.parcels_counted} parcels processed {rep.window.from} – {rep.window.to}, counted in <b>{unitWord}</b> from <b>{fromLabel}</b>.
            </div>
            <div className="text-gray-400 mt-1">
              {unit === 'workdays'
                ? 'Workdays = Monday–Friday that are not a public holiday in Estonia or in the customer\'s country. Friday → Tuesday = 2 workdays.'
                : 'Days = calendar days between the two dates. Monday → Thursday = 3 days; the same day = 0.'}
              {' '}Always quote the unit with the number — 2.0 workdays and 2.0 days are different answers.
            </div>
            {Object.values(rep.excluded).some(n => n > 0) && (
              <div className="text-gray-500 mt-1">
                Not counted: {Object.entries(rep.excluded).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${EXCLUDED_TEXT[k] || k}`).join(', ')}.
              </div>
            )}
            {unit === 'workdays' && !rep.workdays_available && (
              <div className="text-red-400 mt-1">The holiday calendar is missing on the server — workdays can't be counted. Switch to Days.</div>
            )}
            {rep.calendar_gaps && <div className="text-amber-400 mt-1">No holiday calendar for {rep.calendar_gaps.join(', ')} — only Estonia's holidays are skipped there.</div>}
            <details className="mt-2">
              <summary className="text-gray-400 cursor-pointer hover:text-white select-none">How we count</summary>
              <dl className="mt-2 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5">
                {rep.definitions.map(d => (
                  <React.Fragment key={d.term}>
                    <dt className="text-gray-300 font-medium">{d.term}</dt>
                    <dd className="text-gray-400">{d.meaning}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </details>
          </div>

          {/* Table 1 */}
          <div className={card}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div>
                <div className="text-white font-semibold">1 · From {fromLabel} to each step</div>
                <div className="text-gray-500 text-xs">Average {unitWord} per carrier · med = median · n = parcels counted (“of” = got this far)</div>
              </div>
              <button onClick={csv1} className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg">↓ CSV</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr>
                  <th className={th}>Carrier</th><th className={th}>Parcels</th>
                  {steps.map(([k, l]) => <th key={k} className={th}>→ {l}<div className="font-normal normal-case text-gray-600">{unitWord}</div></th>)}
                </tr></thead>
                <Table1Body rows={[...rep.rows, ...(rep.total ? [rep.total] : [])]} />
              </table>
            </div>
          </div>

          {/* Table 2 */}
          <div className={card}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div>
                <div className="text-white font-semibold">2 · Each step on its own</div>
                <div className="text-gray-500 text-xs">Average {unitWord} between two steps — where the time goes (ours, the carrier's, the customer's)</div>
              </div>
              <button onClick={csv2} className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg">↓ CSV</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead><tr>
                  <th className={th}>Carrier</th>
                  {legs.map(([k, l]) => (
                    <th key={k} className={th}>{l.label}
                      <div className="font-normal text-gray-600">{l.span}{l.only ? ` · ${l.only === 'point' ? 'pickup-point parcels' : 'home deliveries'}` : ''}</div>
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {[...rep.rows, ...(rep.total ? [rep.total] : [])].map((r, i) => (
                    <tr key={`${r.carrier}-${i}`} className={`border-t border-gray-800/60 ${r.carrier == null ? 'bg-gray-900/40' : ''}`}>
                      <td className={`${td} ${r.carrier == null ? 'text-gray-300 font-semibold' : 'text-white'}`}>{r.carrier_name}</td>
                      {legs.map(([k]) => (
                        <td key={k} className={td}>
                          <CellView c={r.legs[k]} unit={unit} />
                          {k === 'to_carrier' && r.legs[k]?.within_expectation && (
                            <div className="text-[11px] text-gray-400 mt-0.5"
                                 title={`share of parcels whose first scan came within ${r.legs[k].within_expectation!.limit_workdays} workday(s) of processing (Settings → Shipping → Tracking)`}>
                              {Math.round(r.legs[k].within_expectation!.share * 100)}% within {r.legs[k].within_expectation!.limit_workdays} wd
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 3 */}
          <div className={card}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div>
                <div className="text-white font-semibold">3 · Per country</div>
                <div className="text-gray-500 text-xs">Table 1 for each destination country · “—” = the carrier sent nothing there in this period</div>
              </div>
              <div className="flex items-center gap-2">
                <select value={country} onChange={e => setCountry(e.target.value)} className={sel}>
                  <option value="">All countries</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={csv3} className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg">↓ CSV</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[780px]">
                <thead><tr>
                  <th className={th}>Country</th><th className={th}>Carrier</th><th className={th}>Parcels</th>
                  {steps.map(([k, l]) => <th key={k} className={th}>→ {l}<div className="font-normal normal-case text-gray-600">{unitWord}</div></th>)}
                </tr></thead>
                <Table1Body rows={t3rows} withCountry />
              </table>
            </div>
          </div>

          {rep.notes.length > 0 && (
            <div className="mt-4 text-xs text-gray-500 space-y-1">
              {rep.notes.map(n => <div key={n.carrier}><span className="text-gray-300">{n.carrier_name}:</span> {n.note}</div>)}
              <div>Ready for pickup and customer pickup count pickup-point parcels only; home deliveries are in “Home delivery”.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
